// KT400-Segmentierungs-Zielseiten (KlickTipp-SmartLinks): Team, Privat, Themenpause.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";

const SEGMENTS = {
  team: {
    headline: "Du hast es erklärt. Und trotzdem kommt die Frage: „Was machen wir jetzt genau?“",
    text: "Vielleicht fehlt nicht noch eine längere Erklärung. Vielleicht ist noch nicht klar, was für den nächsten Schritt wichtig ist: das Ziel, die Entscheidung oder die konkrete Aufgabe.",
    impuls: "Beim nächsten Teamgespräch kurz innehalten und prüfen: Was soll nach meiner Erklärung für alle klar sein?",
  },
  privat: {
    headline: "Du willst etwas klären. Und am Ende redet ihr aneinander vorbei.",
    text: "Das heißt nicht automatisch, dass jemand es böse meint oder du dich nur besser ausdrücken musst. Manchmal achten zwei Menschen im selben Gespräch auf unterschiedliche Dinge.",
    impuls: "Vor dem nächsten Gespräch kurz fragen: Was möchte ich klären, und was soll mein Gegenüber danach verstanden haben?",
  },
};

const useNoIndex = (title) => useEffect(() => {
  document.title = title;
  const m = document.createElement("meta");
  m.name = "robots";
  m.content = "noindex, nofollow";
  document.head.appendChild(m);
  return () => m.remove();
}, [title]);

export function withKt400Params(url, segment) {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" || !u.hostname.includes(".")) return null;
    u.searchParams.set("utm_campaign", "kt400");
    u.searchParams.set("utm_content", segment);
    return u.toString();
  } catch {
    return null;
  }
}

const Shell = ({ children, testid }) => (
  <div className="min-h-screen bg-[#FAF7F2]" data-testid={testid}>
    <div className="mx-auto max-w-xl px-5 pt-10 pb-16 sm:pt-16">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500" data-testid="kt400-brand">Flex4Success</p>
      {children}
      <footer className="mt-12 flex gap-4 text-xs text-slate-500" data-testid="kt400-footer">
        <a href="/impressum" className="inline-flex items-center min-h-[44px] px-1 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 rounded" data-testid="kt400-impressum-link">Impressum</a>
        <a href="/datenschutz" className="inline-flex items-center min-h-[44px] px-1 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 rounded" data-testid="kt400-datenschutz-link">Datenschutz</a>
      </footer>
    </div>
  </div>
);

function OfferCta({ segment }) {
  const [url, setUrl] = useState(undefined);
  const [label, setLabel] = useState("Angebot ansehen");
  useEffect(() => {
    api.get("/kt400/config").then((r) => {
      setUrl(r.data?.offer_url || null);
      if (r.data?.cta_labels?.[segment]) setLabel(r.data.cta_labels[segment]);
    }).catch(() => setUrl(null));
  }, [segment]);
  const base = "mt-10 w-full sm:w-auto inline-flex items-center justify-center rounded-full px-8 py-4 text-lg font-bold focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-slate-900";
  const href = url ? withKt400Params(url, segment) : null;
  if (href) {
    return (
      <a href={href} className={`${base} bg-[#F3651D] text-white hover:bg-[#d9551a] transition-colors`}
        data-testid="kt400-offer-cta">{label}</a>
    );
  }
  return (
    <button type="button" disabled aria-disabled="true" className={`${base} bg-slate-200 text-slate-600 cursor-not-allowed`}
      data-testid="kt400-offer-cta-disabled">{url === undefined ? "…" : "Angebot folgt"}</button>
  );
}

function SegmentPage({ segment }) {
  const s = SEGMENTS[segment];
  useNoIndex("Flex4Success");
  return (
    <Shell testid={`kt400-${segment}-page`}>
      <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold leading-tight text-slate-900" data-testid="kt400-headline">{s.headline}</h1>
      <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-700" data-testid="kt400-text">{s.text}</p>
      <div className="mt-8 rounded-2xl bg-white border border-slate-200 border-l-4 border-l-[#F3651D] p-5 shadow-sm" data-testid="kt400-impuls">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          <Lightbulb className="w-4 h-4 text-[#F3651D]" aria-hidden="true" /> Dein Impuls
        </p>
        <p className="mt-2 text-base sm:text-lg font-semibold leading-relaxed text-slate-900">{s.impuls}</p>
      </div>
      <OfferCta segment={segment} />
    </Shell>
  );
}

export const KT400Team = () => <SegmentPage segment="team" />;
export const KT400Privat = () => <SegmentPage segment="privat" />;

// Weg nur fuer bestehende Konten (Login mit silent_unknown), Eingeloggte leitet /login ins Cockpit.
// Button statt <a>: GA-url_passthrough schreibt <a>-hrefs um (in der Vorschau dann vom Badge-CSS versteckt).
function PauseLink() {
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  useEffect(() => { api.get("/kt400/config").then((r) => setLink(r.data?.pause_link || null)).catch(() => {}); }, []);
  if (!link?.enabled) return null;
  return (
    <button type="button" onClick={() => navigate("/login?next=/cockpit&from=kt400-pause")} className="mt-8 inline-flex items-center min-h-[44px] text-sm font-semibold text-slate-700 underline underline-offset-4 decoration-[#F3651D] hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 rounded"
      data-testid="kt400-pause-cockpit-link">{link.label}</button>
  );
}

export function KT400Pause() {
  useNoIndex("Flex4Success");
  return (
    <Shell testid="kt400-pause-page">
      <div className="mt-6 w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center">
        <Check className="w-6 h-6 text-[#F3651D]" aria-hidden="true" />
      </div>
      <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold leading-tight text-slate-900" data-testid="kt400-headline">Alles klar.</h1>
      <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-700" data-testid="kt400-text">
        Wir pausieren weitere Angebote zu diesem Thema für die nächsten 90 Tage.
      </p>
      <PauseLink />
    </Shell>
  );
}

const CHOICES = ["team", "privat", "pause"];

function EmailField({ value, onChange }) {
  return (
    <label className="mt-8 block text-sm font-semibold text-slate-700">
      Deine E-Mail-Adresse
      <input type="email" value={value} onChange={(e) => onChange(e.target.value)} autoComplete="email"
        className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
        data-testid="kt400-wahl-email-input" />
    </label>
  );
}

export function KT400Wahl() {
  useNoIndex("Flex4Success");
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [known] = useState(() => sessionStorage.getItem("kt400_email") || "");
  const [email, setEmail] = useState(known);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/kt400/config").then((r) => setCfg(r.data?.choice || {})).catch(() => setCfg({})); }, []);

  const choose = async (segment) => {
    setBusy(segment); setError("");
    try {
      const { data } = await api.post("/kt400/choice", { email, segment });
      navigate(data.redirect);
    } catch (e) {
      setError(e?.response?.data?.detail || "Das hat nicht geklappt. Bitte versuche es noch einmal.");
      setBusy(null);
    }
  };

  return (
    <Shell testid="kt400-wahl-page">
      <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold leading-tight text-slate-900" data-testid="kt400-wahl-headline">
        {cfg?.choice_headline || "…"}
      </h1>
      {!known && <EmailField value={email} onChange={setEmail} />}
      <div className="mt-8 flex flex-col gap-3">
        {cfg && CHOICES.map((seg) => (
          <button key={seg} type="button" disabled={!!busy || !email} onClick={() => choose(seg)}
            className={`w-full min-h-[56px] rounded-2xl px-6 py-4 text-left text-lg font-bold transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-slate-900 disabled:opacity-60 ${seg === "pause" ? "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50" : "bg-[#F3651D] text-white hover:bg-[#d9551a]"}`}
            data-testid={`kt400-wahl-${seg}-btn`}>
            {busy === seg ? "Einen Moment …" : cfg[`choice_label_${seg}`]}
          </button>
        ))}
      </div>
      {error && <p role="alert" className="mt-4 text-sm font-semibold text-red-700" data-testid="kt400-wahl-error">{error}</p>}
    </Shell>
  );
}

export default KT400Team;
